import { Dialog, Transition } from "@headlessui/react";
import { Link } from "react-router-dom";
import React, { Fragment, useState } from "react";
import { AiOutlineDelete } from "react-icons/ai";
import { FaBagShopping } from "react-icons/fa6";
import { MdClose, MdStar } from "react-icons/md";

import type { CartItem } from "@/context/CartContext";
import { useCart } from "@/context/CartContext";
import ButtonCircle3 from "@/shared/Button/ButtonCircle3";
import ButtonPrimary from "@/shared/Button/ButtonPrimary";
import InputNumber from "@/shared/InputNumber/InputNumber";

import LikeButton from "./LikeButton";

const CartSideBar: React.FC = () => {
  const { totalItems, cart } = useCart();
  const [isVisible, setIsVisible] = useState(false);

  const openMenu = () => setIsVisible(true);
  const closeMenu = () => setIsVisible(false);

  const renderProduct = (item: CartItem) => {
    const { name, price, slug, rating, shoeCategory, coverImage } = item;

    return (
      <div key={name} className="flex py-5 last:pb-0">
        <div className="relative size-24 shrink-0 overflow-hidden rounded-xl">
          <img
            src={coverImage}
            alt={name}
            className="size-full object-contain object-center"
          />
          <Link
            to={`/products/${slug}`}
            onClick={closeMenu}
            className="absolute inset-0"
          />
        </div>

        <div className="ml-4 flex flex-1 flex-col justify-between">
          <div>
            <div className="flex justify-between">
              <div>
                <h3 className="font-medium">
                  <Link to={`/products/${slug}`} onClick={closeMenu}>
                    {name}
                  </Link>
                </h3>
                <p className="my-1 text-sm text-neutral-500">{shoeCategory}</p>
                <div className="flex items-center gap-1">
                  <MdStar className="text-yellow-400" />
                  <span className="text-sm">{rating}</span>
                </div>
              </div>
              <span className="font-medium">${price}</span>
            </div>
          </div>

          <div className="flex w-full items-end justify-between text-sm">
            <div className="flex items-center gap-3">
              <LikeButton />
              <AiOutlineDelete className="text-2xl" />
            </div>
            <InputNumber />
          </div>
        </div>
      </div>
    );
  };

  const renderContent = () => (
    <Transition appear show={isVisible} as={Fragment}>
      <Dialog
        as="div"
        className="fixed inset-0 z-50 overflow-y-auto"
        onClose={closeMenu}
      >
        <div className="fixed inset-y-0 right-0 z-max w-full max-w-md outline-none md:max-w-md">
          <Transition.Child
            as={Fragment}
            enter="transition duration-100 transform"
            enterFrom="opacity-0 translate-x-full"
            enterTo="opacity-100 translate-x-0"
            leave="transition duration-150 transform"
            leaveFrom="opacity-100 translate-x-0"
            leaveTo="opacity-0 translate-x-full"
          >
            <div className="relative z-20">
              <div className="overflow-hidden shadow-lg ring-1 ring-black/5">
                <div className="relative h-screen bg-white">
                  <div className="hiddenScrollbar h-screen overflow-y-auto p-5">
                    <div className="flex items-center justify-between">
                      <h3 className="text-xl font-semibold">Shopping cart</h3>
                      <ButtonCircle3 onClick={closeMenu}>
                        <MdClose className="text-2xl" />
                      </ButtonCircle3>
                    </div>
                    <div className="divide-y divide-neutral-300">
                      {cart.slice(0, 2).map(renderProduct)}
                    </div>
                  </div>

                  <div className="absolute bottom-0 left-0 w-full bg-neutral-50 p-5">
                    <div className="flex justify-between">
                      <div>
                        <p className="font-medium">Subtotal</p>
                        <p className="text-sm text-neutral-500">
                          Shipping and taxes calculated at checkout.
                        </p>
                      </div>
                      <p className="text-xl font-medium">$597</p>
                    </div>
                    <div className="mt-5 flex gap-5">
                      <ButtonPrimary
                        to="/checkout"
                        onClick={closeMenu}
                        className="flex-1"
                      >
                        Checkout
                      </ButtonPrimary>
                      <ButtonPrimary
                        to="/cart"
                        onClick={closeMenu}
                        className="flex-1 border-2 border-primary text-primary"
                      >
                        View cart
                      </ButtonPrimary>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </Transition.Child>

          <Transition.Child
            as={Fragment}
            enter="duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Dialog.Overlay className="fixed inset-0 bg-neutral-900/60" />
          </Transition.Child>
        </div>
      </Dialog>
    </Transition>
  );

  return (
    <>
      <button
        type="button"
        onClick={openMenu}
        className="mx-5 flex items-center gap-1 rounded-full bg-neutral-100 p-2 text-primary focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-opacity-75"
      >
        <FaBagShopping className="text-2xl" />
        <span className="hidden text-sm lg:block">{totalItems} items</span>
      </button>

      {renderContent()}
    </>
  );
};

export default CartSideBar;
